<template>
  <div
    class="m-2 shadow rounded overflow-hidden group"
    :class="hasErrors ? 'bg-red-50 shadow-outline-red' : 'bg-white'"
  >
    <div class="p-5 flex">
      <div
        class="text-green-400 lg:text-green-300 group-hover:text-green-400 text-xl handle cursor-move"
      >
        <font-awesome-icon icon="grip-vertical"></font-awesome-icon>
      </div>
      <div class="ml-4 w-full">
        <div class="flex flex-col md:flex-row justify-between w-full">
          <div class="flex md:block justify-between">
            <div class="font-medium">
              <span class="text-gray-400" v-if="!type.title">Kein Titel festgelegt</span>
              <span>{{ type.title }}</span>
              <button
                class="ml-2 text-sm text-gray-800 lg:invisible group-hover:visible focus:outline-none"
                @click="open = !open"
              >
                <font-awesome-icon :icon="['fad', 'edit']"></font-awesome-icon>
              </button>
            </div>
            <div class="flex text-sm items-center text-gray-600" v-if="beforePublishedFrom">
              <font-awesome-icon class="text-yellow-500" :icon="['fad', 'alarm-clock']"></font-awesome-icon>
              <span class="ml-2">Verkauf geplant</span>
            </div>
            <div class="flex text-sm items-center text-gray-600" v-else-if="afterPublishedTo">
              <font-awesome-icon class="text-red-500" :icon="['fad', 'times-circle']"></font-awesome-icon>
              <span class="ml-2">Verkauf beendet</span>
            </div>
            <div class="flex text-sm items-center text-gray-600" v-else>
              <font-awesome-icon class="text-green-500" :icon="['fad', 'check-circle']"></font-awesome-icon>
              <span class="ml-2">Aktiv</span>
            </div>
          </div>

          <div class="flex mt-1 md:mt-0">
            <div class="mr-4 md:mr-0">
              <div>Kostenlos</div>
              <div class="text-sm text-gray-600">keine Gebühren</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-show="open" class="bg-gray-50 px-4 py-5 sm:p-6">
      <div class="grid grid-cols-1 row-gap-6 col-gap-4 sm:grid-cols-8">
        <basic-input
          placeholder="z.B. Standard"
          v-model="type.title"
          class="sm:col-span-4"
          label="Titel"
          :error="errors[`${errorKey}.title`]"
        />

        <basic-input
          placeholder="z.B. Schüler und Studenten"
          corner-hint="Optional"
          v-model="type.description"
          class="sm:col-span-4"
          label="Info"
          :error="errors[`${errorKey}.description`]"
        />

        <basic-date-picker
          v-model="type.published_from.utc"
          corner-hint="Optional"
          format="d.m.Y H:i"
          class="sm:col-span-4 lg:col-span-2"
          label="Verkaufsstart"
          :error="errors[`${errorKey}.published_from.utc`]"
        />

        <basic-date-picker
          v-model="type.published_to.utc"
          corner-hint="Optional"
          format="d.m.Y H:i"
          class="sm:col-span-4 lg:col-span-2"
          label="Verkaufsende"
          :error="errors[`${errorKey}.published_to.utc`]"
          :min-date="
            type.published_from.utc ? type.published_from.utc : new Date()
          "
        />

        <div class="sm:col-span-4">
          <label
            class="block text-sm font-medium leading-5 text-gray-700"
          >Pro Bestellung zulässige Tickets</label>

          <div class="flex">
            <basic-input
              v-model="type.min_amount_per_order"
              class="w-full -mr-px"
              hint="Minimum"
              type="number"
              min="1"
              input-class="rounded-none rounded-l-md relative focus:z-10"
              :error="errors[`${errorKey}.min_amount_per_order`]"
            />
            <basic-input
              v-model="type.max_amount_per_order"
              class="w-full"
              hint="Maximum"
              type="number"
              min="1"
              input-class="rounded-none rounded-r-md relative focus:z-10"
              :error="errors[`${errorKey}.max_amount_per_order`]"
            />
          </div>
        </div>
      </div>
      <div class="flex mt-6">
        <basic-button class="mr-4" @click="open = false" color="white">Schließen</basic-button>
        <basic-button color="red" design="secondary" @click="destroy()" leading-icon="trash">Löschen</basic-button>
      </div>
    </div>
  </div>
</template>

<script>
import BasicDatePicker from "@/components/basic/DatePicker";
import DeleteConfirmationModal from "@/components/modals/DeleteConfirmationModal";

import _startsWith from "lodash/startsWith";
import _some from "lodash/some";

import dayjs from "dayjs";

import typeClient from "@/api/central/types";

export default {
  props: {
    type: {
      required: true,
      type: Object
    },
    errors: {
      required: true,
      type: Object
    },
    errorKey: {
      required: true,
      type: String
    },
    ticketFees: {
      required: true,
      type: Array
    }
  },

  components: { BasicDatePicker },

  data() {
    return {
      open: !!this.type._id
    };
  },

  computed: {
    beforePublishedFrom() {
      return dayjs.utc().isBefore(dayjs.utc(this.type.published_from.utc));
    },
    afterPublishedTo() {
      return dayjs.utc().isAfter(dayjs.utc(this.type.published_to.utc));
    },
    hasErrors() {
      return _some(this.errors, (value, key) => {
        return _startsWith(key, this.errorKey);
      });
    }
  },
  methods: {
    destroy() {
      let func = () => {
        let promise = this.type.id
          ? typeClient.destroy(this.type.id)
          : Promise.resolve(); // Make empty promise if type was not saved on server

        return promise
          .then(async () => {
            await this.$store.dispatch("editor/refresh");

            this.$toasted.global.success();
            this.$emit("destroyed");
          })
          .catch(() => {
            this.$toasted.global.error();
          });
      };

      this.$modal.show(DeleteConfirmationModal, {
        destroyFunc: func,
        header: `${this.type.title || "Tickettyp"} wirklich löschen?`
      });
    }
  }
};
</script>
